import {
  Debt,
  DebtorGroup,
  DebtStatus,
  formatCurrency,
  Big,
  bigSum,
} from '@bofrak-backend/shared';
import {
  Badge,
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { formatDate } from '@bofrak-backend/shared-i18n';
import { useSharedUiTranslation } from '../../../i18n';
import { getDebtStatusLabel } from '../../utils/status-labels';

interface DebtorDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  debtorGroup: DebtorGroup | null;
}

const STATUS_COLORS: Record<DebtStatus, string> = {
  [DebtStatus.ACTIVE]: 'blue',
  [DebtStatus.OVERDUE]: 'red',
  [DebtStatus.DEFAULTED]: 'red',
  [DebtStatus.CLOSED]: 'green',
  [DebtStatus.CANCELLED]: 'gray',
  [DebtStatus.TRANSFERRED]: 'gray',
  [DebtStatus.REVERSED]: 'orange',
};

export function DebtorDrawer({
  isOpen,
  onClose,
  debtorGroup,
}: DebtorDrawerProps) {
  const { t } = useSharedUiTranslation();
  if (!debtorGroup) return null;

  const { debts, total_outstanding_amount, total_paid_amount } = debtorGroup;
  const name = debts[0]?.debtor_name ?? t('Unknown');

  const closedAmount = bigSum(
    debts
      .filter((d) => d.status === DebtStatus.CLOSED)
      .map((d) => d.principal_amount),
  ).toNumber();

  const overdueAmount = bigSum(
    debts
      .filter((d) => d.status === DebtStatus.OVERDUE)
      .map((d) => d.outstanding_balance),
  ).toNumber();

  const defaultedAmount = bigSum(
    debts
      .filter((d) => d.status === DebtStatus.DEFAULTED)
      .map((d) => d.outstanding_balance),
  ).toNumber();

  const interestTotal = bigSum(
    debts.map((d) =>
      new Big(d.interest_rate).div(100).times(d.principal_amount),
    ),
  ).toNumber();

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader pb={1}>
          <Text fontSize="md" fontWeight="bold">
            {name}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {t('{{count}} debt', { count: debts.length })}
          </Text>
        </DrawerHeader>

        <DrawerBody px={3} pb={6}>
          {/* Summary Stats */}
          <SimpleGrid columns={3} spacing={2} mb={4}>
            <StatBox
              label={t('Closed')}
              value={closedAmount}
              color="green.500"
            />
            <StatBox
              label={t('Unpaid')}
              value={total_outstanding_amount}
              color={total_outstanding_amount > 0 ? 'red.500' : 'green.500'}
            />
            <StatBox
              label={t('Overdue')}
              value={overdueAmount}
              color={overdueAmount > 0 ? 'red.500' : 'green.500'}
            />
            <StatBox
              label={t('Interest')}
              value={interestTotal}
              color="blue.500"
            />
            <StatBox
              label={t('Defaulted')}
              value={defaultedAmount}
              color={defaultedAmount > 0 ? 'red.500' : 'gray.500'}
            />
            <StatBox
              label={t('Total Paid')}
              value={total_paid_amount}
              color="green.500"
            />
          </SimpleGrid>

          {/* Debt Cards */}
          <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2}>
            {t('DEBTS')}
          </Text>
          <VStack spacing={2} align="stretch">
            {debts.map((debt) => (
              <DebtCard key={debt.id} debt={debt} />
            ))}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

function StatBox({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <Box bg="gray.50" borderRadius="md" p={2} textAlign="center">
      <Text fontSize="sm" fontWeight="bold" color={color}>
        {formatCurrency(value, true)}
      </Text>
      <Text fontSize="9px" color="gray.500">
        {label}
      </Text>
    </Box>
  );
}

function DebtCard({ debt }: { debt: Debt }) {
  const { t } = useSharedUiTranslation();
  const progressTotal = new Big(debt.principal_amount)
    .plus(new Big(debt.interest_rate).div(100).times(debt.principal_amount))
    .toNumber();
  const paid = new Big(progressTotal)
    .minus(debt.outstanding_balance)
    .toNumber();
  const progressPct =
    progressTotal > 0
      ? new Big(paid).div(progressTotal).times(100).round(0).toNumber()
      : 0;

  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      p={3}>
      <Flex justify="space-between" align="center" mb={1}>
        <Badge
          colorScheme={STATUS_COLORS[debt.status]}
          variant="subtle"
          fontSize="9px">
          {getDebtStatusLabel(debt.status)}
        </Badge>
        <Text fontSize="sm" color="gray.900">
          {debt.id}
        </Text>
      </Flex>

      <HStack justify="space-between" mb={1}>
        <Box>
          <Text fontSize="xs" color="gray.500">
            {t('Principal')}
          </Text>
          <Text fontSize="sm" fontWeight="bold">
            {formatCurrency(debt.principal_amount, true)}
          </Text>
        </Box>
        <Box textAlign="right">
          <Text fontSize="xs" color="gray.500">
            {t('Outstanding')}
          </Text>
          <Text
            fontSize="sm"
            fontWeight="bold"
            color={debt.outstanding_balance > 0 ? 'red.500' : 'green.500'}>
            {formatCurrency(debt.outstanding_balance, true)}
          </Text>
        </Box>
      </HStack>

      <HStack justify="space-between" fontSize="xs" color="gray.500" mb={1}>
        <Text>
          {t('Created: {{date}}', {
            date: formatDate(new Date(debt.created_at), 'dd MMM yyyy'),
          })}
        </Text>
        {debt.next_due_date && (
          <Text>
            {t('Due: {{date}}', {
              date: formatDate(new Date(debt.next_due_date), 'dd MMM yyyy'),
            })}
          </Text>
        )}
      </HStack>

      {debt.reason && (
        <Text fontSize="xs" color="gray.600" noOfLines={1}>
          {debt.reason}
        </Text>
      )}

      {/* Mini progress */}
      <Box mt={1}>
        <Flex justify="space-between" fontSize="9px" color="gray.400" mb={0.5}>
          <Text>{t('{{percent}}% paid', { percent: progressPct })}</Text>
          <Text>
            {formatCurrency(paid, true)} / {formatCurrency(progressTotal, true)}
          </Text>
        </Flex>
        <Box h="3px" bg="gray.100" borderRadius="full">
          <Box
            h="3px"
            bg={
              progressPct >= 70
                ? 'green.400'
                : progressPct >= 40
                  ? 'yellow.400'
                  : 'red.400'
            }
            borderRadius="full"
            w={`${progressPct}%`}
            transition="width 0.3s"
          />
        </Box>
      </Box>
    </Box>
  );
}
